import type { Hauler, Profile } from '@alliance-disposal/transport-types';
import { useDisposalSites } from '../../hooks/queries/use-disposal-sites';

export type OnboardingStep = 'profile' | 'account' | 'sizes' | 'disposal-site' | 'materials';

type DeriveInput = {
    userProfile: Pick<Profile.ProfileTransport, 'firstName' | 'lastName'> | null;
    hauler: Pick<Hauler.HaulerTransport, 'waysteLite' | 'materials'> | null;
    hasDisposalSites: boolean;
};

const isBlank = (value?: string | null) => !value?.trim();

/**
 * The disposal-site step and the query that decides whether to show it must agree, so both read
 * this rather than restating the condition.
 */
const needsDisposalSiteStep = (hauler: Pick<Hauler.HaulerTransport, 'waysteLite' | 'materials'> | null): boolean =>
    Boolean(hauler && !hauler.waysteLite && hauler.materials.length === 0);

/**
 * Partner Portal users are invited by Sourgum with only an email, so they fill in their own name.
 * Wayste users are added by an admin who supplies it, which is why the profile step is theirs alone.
 */
export const deriveOnboardingSteps = ({ userProfile, hauler, hasDisposalSites }: DeriveInput): OnboardingStep[] => {
    if (!userProfile || !hauler) return [];

    const steps: OnboardingStep[] = [];

    if (hauler.waysteLite && (isBlank(userProfile.firstName) || isBlank(userProfile.lastName))) {
        steps.push('profile');
    }

    if (hauler.materials.length === 0) {
        steps.push('account');
        if (needsDisposalSiteStep(hauler) && !hasDisposalSites) steps.push('disposal-site');
        // Only Partner Portal haulers declare their sizes; a Wayste hauler's are derived from their containers.
        if (hauler.waysteLite) steps.push('sizes');
        steps.push('materials');
    }

    return steps;
};

/**
 * `hauler` is passed in rather than read from `useCurrentAccount`, which asserts a hauler is already
 * loaded and throws otherwise — this hook runs on every render of App, including for signed-out
 * visitors and before the hauler fetch resolves.
 */
export const useOnboardingSteps = (userProfile: Profile.ProfileTransport | null, hauler: Hauler.HaulerTransport | null) => {
    // Only Wayste haulers mid-setup can reach the disposal-site step, so nothing else pays for the query.
    const needsDisposalSiteCheck = needsDisposalSiteStep(hauler);
    const { disposalSites, isLoading } = useDisposalSites({ enabled: needsDisposalSiteCheck });

    const isResolving = needsDisposalSiteCheck && isLoading;

    return {
        isLoading: isResolving,
        // Empty until the query settles: a caller that froze this list mid-flight would capture a
        // disposal-site step the hauler does not need, and never re-read it.
        steps: isResolving
            ? []
            : deriveOnboardingSteps({
                  userProfile,
                  hauler,
                  hasDisposalSites: (disposalSites?.length ?? 0) > 0,
              }),
    };
};
